import { toNonEmptyText } from '../utils';

interface ApiConfig {
  baseURL: string;
  defaultHeaders: Record<string, string>;
  timeout: number;
}

interface RequestOptions {
  method?: HttpMethod;
  headers?: Record<string, string>;
  body?: any;
  params?: Record<string, string | number | boolean>;
  signal?: AbortSignal;
  cache?: RequestCache;
  timeout?: number;
}

const API_CONFIG: ApiConfig = {
  baseURL: '',
  defaultHeaders: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
};

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestOptions {
  method?: HttpMethod;
  headers?: Record<string, string>;
  body?: any;
  params?: Record<string, string | number | boolean>;
  signal?: AbortSignal;
  cache?: RequestCache;
  timeout?: number;
}

export class ApiError extends Error {
  status: number;
  data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

interface ApiErrorBody {
  title?: string;
  detail?: string;
  message?: string;
  errors?: Array<{
    field?: string;
    message?: string;
  }>;
}

function parseResponseBody(text: string): ApiErrorBody | string | null {
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function getResponseErrorMessage(
  response: Response,
  body: ApiErrorBody | string | null,
): string {
  const statusText = toNonEmptyText(response.statusText);

  if (body && typeof body !== 'string') {
    const serverMessages = [
      body.errors?.[0]?.message,
      body.detail,
      body.title,
      body.message,
    ];

    for (const message of serverMessages) {
      const err = toNonEmptyText(message);
      if (err) return err;
    }
  }

  if (statusText) return statusText;

  const textBody = toNonEmptyText(body);
  if (textBody) return textBody;

  return 'An error occurred during the request';
}

const handleResponse = async <T>(response: Response): Promise<T> => {
  const text = await response.text();

  if (!response.ok) {
    const body = parseResponseBody(text);

    throw new ApiError(
      getResponseErrorMessage(response, body),
      response.status,
      body,
    );
  }

  if (response.status === 204 || !text) return undefined as T;
  return JSON.parse(text) as T;
};

const createUrl = (endpoint: string, baseURL: string): string => {
  const url = endpoint.startsWith('http')
    ? endpoint
    : `${baseURL}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;

  return url;
};

const prepareHeaders = (
  customHeaders: Record<string, string> = {},
  token: string,
): Headers => {
  const headers = new Headers({
    ...API_CONFIG.defaultHeaders,
    ...customHeaders,
  });

  if (token) {
    headers.append('Authorization', `Bearer ${token}`);
  }

  return headers;
};

const request = async <T>(
  endpoint: string,
  baseURL: string,
  authToken: string,
  options: RequestOptions = {},
): Promise<T> => {
  try {
    const url = createUrl(endpoint, baseURL);
    const headers = prepareHeaders(options.headers, authToken);

    const config: RequestInit = {
      method: options.method || 'GET',
      headers,
      signal: options.signal,
    };

    if (options.body && config.method !== 'GET') {
      const isFormData = options.body instanceof FormData;

      if (isFormData) {
        // Let the browser set the correct multipart boundary
        headers.delete('Content-Type');
        config.body = options.body;
      } else {
        config.body =
          typeof options.body === 'string'
            ? options.body
            : JSON.stringify(options.body);
      }
    }

    let finalUrl = url;
    if (options.params && Object.keys(options.params).length > 0) {
      const queryParams = new URLSearchParams();
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
      finalUrl = `${url}${
        url.includes('?') ? '&' : '?'
      }${queryParams.toString()}`;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      options.timeout || API_CONFIG.timeout,
    );

    if (!options.signal) {
      config.signal = controller.signal;
    }

    const response = await fetch(finalUrl, config);
    clearTimeout(timeoutId);

    return await handleResponse<T>(response);
  } catch (error: any) {
    if (error.name === 'AbortError') {
      throw new ApiError('Request timeout', 408);
    }
    // eslint-disable-next-line no-console
    console.log(error, 'error');
    throw error;
  }
};

export const get = <T>(
  endpoint: string,
  baseURL: string,
  authToken: string,
  options: Omit<RequestOptions, 'method' | 'body'> = {},
): Promise<T> => {
  return request<T>(endpoint, baseURL, authToken, {
    ...options,
    method: 'GET',
  });
};

export const post = <T>(
  endpoint: string,
  body: any,
  baseURL: string,
  authToken: string,
  options: Omit<RequestOptions, 'method' | 'body'> = {},
): Promise<T> => {
  return request<T>(endpoint, baseURL, authToken, {
    ...options,
    method: 'POST',
    body,
  });
};

export const put = <T>(
  endpoint: string,
  body: any,
  baseURL: string,
  authToken: string,
  options: Omit<RequestOptions, 'method' | 'body'> = {},
): Promise<T> => {
  return request<T>(endpoint, baseURL, authToken, {
    ...options,
    method: 'PUT',
    body,
  });
};

export const patch = <T>(
  endpoint: string,
  body: any,
  baseURL: string,
  authToken: string,
  options: Omit<RequestOptions, 'method' | 'body'> = {},
): Promise<T> => {
  return request<T>(endpoint, baseURL, authToken, {
    ...options,
    method: 'PATCH',
    body,
  });
};

export const del = <T>(
  endpoint: string,
  baseURL: string,
  authToken: string,
  options: Omit<RequestOptions, 'method'> = {},
): Promise<T> => {
  return request<T>(endpoint, baseURL, authToken, {
    ...options,
    method: 'DELETE',
  });
};

interface ApiService {
  get: <T>(
    endpoint: string,
    baseURL: string,
    authToken: string,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ) => Promise<T>;
  post: <T>(
    endpoint: string,
    body: any,
    baseURL: string,
    authToken: string,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ) => Promise<T>;
  put: <T>(
    endpoint: string,
    body: any,
    baseURL: string,
    authToken: string,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ) => Promise<T>;
  patch: <T>(
    endpoint: string,
    body: any,
    baseURL: string,
    authToken: string,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ) => Promise<T>;
  delete: <T>(
    endpoint: string,
    baseURL: string,
    authToken: string,
    options?: Omit<RequestOptions, 'method'>,
  ) => Promise<T>;
}

const apiService: ApiService = {
  get,
  post,
  put,
  patch,
  delete: del,
};

export default apiService;
